/**
 * CalendarSyncSettings Component
 * 
 * Settings page for Calendar Integration including:
 * - Calendar Auto-Sync toggle
 * - Sync All Dates batch operation with review screen
 * - Category selection for sync
 * - Progress tracking
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CalendarDays, 
  CalendarCheck, 
  CalendarX,
  RefreshCw, 
  AlertCircle, 
  CheckCircle2,
  Loader2,
  XCircle,
  Calendar,
  Cake,
  Users,
  Building,
  CreditCard,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';
import {
  isNativeCalendarAvailable,
  requestCalendarPermission,
  syncDateToCalendar,
  CALENDAR_PERMISSION,
} from '@/services/nativeCalendarService';

// Category configuration
const CATEGORIES = [
  { id: 'family', label: 'Shared Events', icon: Users, color: 'text-green-600' },
  { id: 'birthday', label: 'Birthdays', icon: Cake, color: 'text-pink-600' },
  { id: 'appointment', label: 'Appointments', icon: Calendar, color: 'text-purple-600' },
  { id: 'reservation', label: 'Reservations', icon: Building, color: 'text-blue-600' },
  { id: 'community', label: 'Community', icon: Heart, color: 'text-orange-600' },
  { id: 'membership', label: 'Memberships', icon: CreditCard, color: 'text-teal-600' },
];

const CalendarSyncSettings = () => {
  const { isDemoMode } = useDemo();
  const [loading, setLoading] = useState(false);
  const [autoSync, setAutoSync] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(
    CATEGORIES.map(c => c.id)
  );
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncResults, setSyncResults] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState(null);
  
  const isNative = isNativeCalendarAvailable();

  // Load sync summary when dialog opens
  const loadSyncSummary = useCallback(async () => {
    if (isDemoMode) {
      // Demo mode - show mock data
      setSyncSummary({
        total_eligible: 12,
        already_synced: 3,
        to_be_synced: 7,
        incomplete_data: 2,
        upcoming_dates: [
          { id: '1', title: "Mom's Birthday", date: '2026-02-15', category: 'birthday' },
          { id: '2', title: 'Annual Checkup', date: '2026-01-20', category: 'appointment' },
          { id: '3', title: 'Netflix Renewal', date: '2026-01-30', category: 'membership' },
        ]
      });
      return;
    }
    
    setLoading(true);
    try {
      const categories = selectedCategories.join(',');
      const response = await API.get(`/dates/sync-summary?categories=${categories}`);
      setSyncSummary(response.data);
    } catch (error) {
      console.error('Failed to load sync summary:', error);
      toast.error('Failed to load dates');
    } finally {
      setLoading(false);
    }
  }, [isDemoMode, selectedCategories]);

  // Handle category toggle
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Open sync dialog and load summary
  const openSyncDialog = async () => {
    setSyncDialogOpen(true);
    setSyncResults(null);
    setSyncProgress(0);
    await loadSyncSummary();
    
    // Check permission status
    if (isNative) {
      const { status } = await requestCalendarPermission();
      setPermissionStatus(status);
    }
  };

  // Perform batch sync
  const performBatchSync = async () => {
    if (isDemoMode) {
      toast.info('Demo Mode: Calendar sync requires a real account', {
        description: 'Sign up to sync dates to your phone calendar.'
      });
      return;
    }
    
    if (!isNative) {
      toast.error('Native calendar not available', {
        description: 'Calendar sync requires the mobile app.'
      });
      return;
    }
    
    // Check permission
    const { granted } = await requestCalendarPermission();
    if (!granted) {
      return; // Permission request already shows error toast
    }
    
    setSyncing(true);
    setSyncProgress(0);
    
    const datesToSync = syncSummary?.upcoming_dates?.filter(d => 
      !d.native_calendar_event_id && selectedCategories.includes(d.category)
    ) || [];
    
    const results = {
      synced: 0,
      alreadySynced: 0,
      failed: 0,
      incomplete: 0,
      details: []
    };
    
    const batchResults = [];
    
    for (let i = 0; i < datesToSync.length; i++) {
      const date = datesToSync[i];
      setSyncProgress(Math.round((i / datesToSync.length) * 100));
      
      try {
        // Fetch full date record
        const response = await API.get(`/dates/${date.id}`);
        const fullDate = response.data;
        
        // Sync to native calendar
        const result = await syncDateToCalendar(fullDate, fullDate.native_calendar_event_id);
        
        if (result.success) {
          results.synced++;
          batchResults.push({
            date_id: date.id,
            event_id: result.eventId,
            status: 'synced'
          });
        } else {
          results.failed++;
          batchResults.push({
            date_id: date.id,
            status: 'failed',
            error: result.error
          });
        }
      } catch (error) {
        console.error(`Failed to sync date ${date.id}:`, error);
        results.failed++;
        batchResults.push({
          date_id: date.id,
          status: 'failed',
          error: error.message
        });
      }
    }
    
    // Send batch results to backend
    try {
      await API.post('/dates/batch-sync', { results: batchResults });
    } catch (error) {
      console.error('Failed to save sync results:', error);
    }
    
    setSyncProgress(100);
    setSyncResults(results);
    setSyncing(false);
    
    // Show result toast
    if (results.synced > 0) {
      toast.success(`${results.synced} dates synced to calendar`, {
        description: results.failed > 0 
          ? `${results.failed} dates couldn't be synced`
          : undefined
      });
    } else if (results.failed > 0) {
      toast.error('Sync failed', {
        description: 'No dates were synced. Please try again.'
      });
    }
  };

  // Handle auto-sync toggle
  const handleAutoSyncToggle = async (enabled) => {
    if (enabled && isNative) {
      const { granted } = await requestCalendarPermission();
      if (!granted) {
        return;
      }
    }
    
    setAutoSync(enabled);
    
    if (isDemoMode) {
      toast.success(enabled ? 'Auto-sync enabled (demo)' : 'Auto-sync disabled (demo)');
      return;
    }
    
    try {
      await API.put('/settings', { calendar_auto_sync: enabled });
      toast.success(
        enabled 
          ? 'Calendar sync enabled' 
          : 'Calendar sync disabled',
        { 
          description: enabled 
            ? 'New dates will sync automatically.' 
            : 'Dates will not sync automatically.'
        }
      );
    } catch (error) {
      console.error('Failed to update setting:', error);
      setAutoSync(!enabled); // Revert
      toast.error('Failed to update setting');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-100 rounded-lg">
          <CalendarDays className="h-6 w-6 text-purple-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Calendar Integration</h2>
          <p className="text-sm text-slate-500">
            Sync your dates with your phone&apos;s calendar
          </p>
        </div>
      </div>

      {/* Platform Notice */}
      {!isNative && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Native Calendar Sync Requires Mobile App
              </p>
              <p className="text-sm text-amber-700 mt-1">
                You&apos;re viewing in a browser. Calendar sync with your phone requires the 
                iOS or Android app. You can still export dates as .ics files.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auto-Sync Toggle */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className={`h-5 w-5 ${autoSync ? 'text-purple-600' : 'text-slate-400'}`} />
              <div>
                <p className="font-medium text-slate-800">Auto-Sync New Dates</p>
                <p className="text-sm text-slate-500">
                  {autoSync 
                    ? 'New dates automatically sync to your calendar'
                    : 'Dates must be synced manually'}
                </p>
              </div>
            </div>
            <Switch
              checked={autoSync}
              onCheckedChange={handleAutoSyncToggle}
              disabled={!isNative && !isDemoMode}
              data-testid="auto-sync-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sync All Dates Button */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarCheck className="h-5 w-5 text-green-600" />
            Sync All Dates
          </CardTitle>
          <CardDescription>
            Batch sync all your upcoming dates to your phone calendar
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <Button
            onClick={openSyncDialog}
            className="w-full bg-purple-600 hover:bg-purple-700"
            disabled={!isNative && !isDemoMode}
            data-testid="sync-all-dates-btn"
          >
            <CalendarDays className="h-4 w-4 mr-2" />
            Sync All Dates
          </Button>
        </CardContent>
      </Card>

      {/* Sync Dialog */}
      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-purple-600" />
              Sync Your Dates
            </DialogTitle>
            <DialogDescription>
              {syncing 
                ? 'Syncing dates to your calendar...'
                : syncResults 
                  ? 'Sync complete'
                  : 'Review dates before syncing to your calendar'}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : syncing ? (
            <div className="py-6 space-y-4">
              <Progress value={syncProgress} className="h-2" />
              <p className="text-center text-sm text-slate-500">
                {syncProgress}% complete
              </p>
            </div>
          ) : syncResults ? (
            <div className="py-4 space-y-4">
              {/* Results Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-green-700">{syncResults.synced}</p>
                  <p className="text-xs text-green-600">Synced</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <CalendarCheck className="h-6 w-6 text-slate-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-slate-600">{syncSummary?.already_synced || 0}</p>
                  <p className="text-xs text-slate-500">Already synced</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-red-600">{syncResults.failed}</p>
                  <p className="text-xs text-red-500">Failed</p>
                </div>
              </div>
              
              {syncResults.incomplete > 0 && (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {syncResults.incomplete} dates need more information
                </p>
              )}
            </div>
          ) : (
            <div className="py-4 space-y-4">
              {/* Summary Stats */}
              {syncSummary && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-700">
                      {syncSummary.to_be_synced}
                    </p>
                    <p className="text-xs text-blue-600">Will be synced</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">
                      {syncSummary.already_synced}
                    </p>
                    <p className="text-xs text-green-600">Already synced</p>
                  </div>
                </div>
              )}
              
              {syncSummary?.incomplete_data > 0 && (
                <div className="flex items-center gap-2 p-2 bg-amber-50 rounded text-amber-700 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {syncSummary.incomplete_data} dates need more information
                </div>
              )}

              {/* Category Selection */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">
                  Categories to sync:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map(cat => (
                    <label 
                      key={cat.id}
                      className="flex items-center gap-2 p-2 rounded-lg border hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedCategories.includes(cat.id)}
                        onCheckedChange={() => toggleCategory(cat.id)}
                      />
                      <cat.icon className={`h-4 w-4 ${cat.color}`} />
                      <span className="text-sm">{cat.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preview List */}
              {syncSummary?.upcoming_dates?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    Preview ({Math.min(5, syncSummary.upcoming_dates.length)} of {syncSummary.upcoming_dates.length}):
                  </p>
                  <ScrollArea className="h-32 rounded border">
                    <div className="p-2 space-y-1">
                      {syncSummary.upcoming_dates.slice(0, 5).map((date, i) => (
                        <div 
                          key={date.id || i}
                          className="flex items-center justify-between p-2 rounded bg-slate-50"
                        >
                          <span className="text-sm truncate flex-1">{date.title}</span>
                          <span className="text-xs text-slate-500 ml-2">
                            {new Date(date.date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Demo Mode Warning */}
              {isDemoMode && (
                <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
                  <strong>Demo Mode:</strong> No real calendar events will be created.
                  Sign up to sync dates to your actual calendar.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {syncResults ? (
              <Button onClick={() => setSyncDialogOpen(false)}>
                Done
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setSyncDialogOpen(false)}
                  disabled={syncing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={performBatchSync}
                  disabled={syncing || !syncSummary?.to_be_synced || selectedCategories.length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="confirm-sync-btn"
                >
                  {syncing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <CalendarCheck className="h-4 w-4 mr-2" />
                      Sync {syncSummary?.to_be_synced || 0} Dates
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarSyncSettings;
